// @ts-nocheck
/**
 * PDP Scrambler Detail - Extended from PDPProductDetail
 * Adds personalization (nameplate) functionality
 */

function juicedEngravingRejected(value) {
  if (!String(value || '').trim()) return false;
  const result = window.JuicedNameplateModeration?.evaluate(value);
  return !result || result.status === 'blocked';
}

class PDPScramblerDetail {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = sectionElement.dataset.sectionId || '';

    this.splideElement = this.section.querySelector('.scrambler-main-splide');
    this.galleryMain = this.section.querySelector('.scrambler-gallery-main');
    this.galleryDotsContainer = this.section.querySelector('.scrambler-gallery-dots');
    this.imageGrid = this.section.querySelector('.scrambler-image-grid');
    this.variantPicker = this.section.querySelector('variant-picker');
    this.variantInput = this.section.querySelector('[name="id"]');
    this.productForm = this.section.querySelector('.product-form');

    this.variants = this.getVariantsData();
    this.currentVariant = this.getCurrentVariant();

    // Thumbnail fill mode (section setting): 'auto' | 'on' | 'off'.
    // - auto: JPG thumbnails fill the bordered square, PNG stays as-is
    // - on: all thumbnails fill
    // - off: no thumbnails fill
    // Per-image Alt-text override (#fill / #nofill) wins over this.
    this.thumbnailFillMode = this.section.dataset.thumbnailFillMode || 'auto';

    // Variant -> color-specific gallery images (native image + gallery_images
    // metafield). Variants without any are absent → they fall back to the
    // full authored product gallery.
    this.variantMediaMap = this.getVariantMediaMap();
    // Authored main-gallery slide HTML, captured once for the fallback path.
    this.authoredSlidesHtml = [];

    // Personalization state
    this.personalizationConfirmed = false;

    this.splide = null;
    this.init();
  }

  getVariantMediaMap() {
    var el = document.getElementById('product-variant-media-' + this.sectionId);
    if (el) {
      try { return JSON.parse(el.textContent) || {}; } catch (e) {}
    }
    return {};
  }

  getVariantsData() {
    var el = document.getElementById('product-variants-' + this.sectionId);
    if (el) {
      try { return JSON.parse(el.textContent); } catch (e) {}
    }
    return [];
  }

  getCurrentVariant() {
    if (this.variantPicker) {
      var json = this.variantPicker.querySelector('script[type="application/json"]');
      if (json) {
        try { return JSON.parse(json.textContent); } catch (e) {}
      }
    }
    return null;
  }

  init() {
    // Capture the authored main-gallery slides once (for the fallback path
    // when a variant has no color-specific images).
    if (this.splideElement && !this.authoredSlidesHtml.length) {
      var authoredList = this.splideElement.querySelector('.splide__list');
      if (authoredList) {
        this.authoredSlidesHtml = Array.prototype.map.call(
          authoredList.children,
          function (slide) { return slide.outerHTML; }
        );
      }
    }

    // Gallery
    if (this.splideElement && window.Splide) {
      // Read arrow visibility from data-attrs set by the section schema.
      // Defaults: arrows OFF on desktop AND mobile (current behaviour).
      var showArrows = this.splideElement.dataset.showArrows === 'true';
      var hideArrowsMobile = this.splideElement.dataset.hideArrowsMobile === 'true';
      this.splide = new window.Splide(this.splideElement, {
        type: 'fade',
        perPage: 1,
        arrows: showArrows,
        pagination: false,
        drag: true,
        speed: 500,
        rewind: true,
        breakpoints: hideArrowsMobile ? {
          768: { arrows: false },
        } : undefined,
      });
      this.splide.mount();
      this.createGalleryDots();
      this.attachImageGridListeners();

      // Build the color-specific gallery for the initial variant (or leave the
      // authored gallery as-is when the variant has no metafield images).
      if (this.currentVariant) this.applyVariantMedia(this.currentVariant);
    }

    if (!window.Splide && this.splideElement) {
      var self = this;
      window.addEventListener('load', function() { self.init(); });
      return;
    }

    this.attachVariantPickerListeners();
    this.attachAddOnListeners();
    this.attachNameplateChoiceLabels();
    this.attachNameplateModeration();
    this.attachNameplatePreview();
    this.attachPersonalizationListeners();
    this.attachFormSubmitHandler();
    this.applySwatchStates();
  }

  // ---- VARIANT PICKER ----

  attachVariantPickerListeners() {
    if (!this.variantPicker) return;

    this.variantPicker.addEventListener('variant-update', (event) => {
      var variant = event.detail?.variant;
      if (variant) this.onVariantChange(variant);
      this.applySwatchStates();
    });

    this.variantPicker.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="radio"]')) return;
      if (this._ignoreChange) return;

      var swatchGroup = event.target.dataset.swatchGroup;
      if (swatchGroup) this.selectGroupOption(swatchGroup);

      this.applySwatchStates();
      this.updateAddToCartForAvailability();

      // Update price when variant changes via radio click
      var variant = this.findVariantFromInputs();
      if (variant) this.onVariantChange(variant);
    });
  }

  applySwatchStates() {
    if (!this.variantPicker) return;

    this.variantPicker.querySelectorAll('.color-swatch-label').forEach((label) => {
      var input = label.querySelector('input[type="radio"]');
      if (input) label.classList.toggle('selected', input.checked);
    });

    this.variantPicker.querySelectorAll('.option-button-label').forEach((label) => {
      var input = label.querySelector('input[type="radio"]');
      if (input) label.classList.toggle('active', input.checked);
    });

    if (!this.variants || this.variants.length === 0) return;

    var fieldsets = this.variantPicker.querySelectorAll('[data-option-position]');
    if (fieldsets.length === 0) return;

    var selectedOptions = this.getSelectedOptions();
    var groupOptionIndex = -1;
    var firstGrouped = this.variantPicker.querySelector('input[data-swatch-group]');
    if (firstGrouped) {
      var groupVal = firstGrouped.dataset.swatchGroup;
      fieldsets.forEach((fs, idx) => {
        if (groupOptionIndex >= 0) return;
        var match = fs.querySelector('input[type="radio"][value="' + CSS.escape(groupVal) + '"]');
        if (match && !match.dataset.swatchGroup) groupOptionIndex = idx;
      });
    }

    var colorFieldsetIndex = firstGrouped
      ? parseInt(firstGrouped.closest('[data-option-position]')?.dataset.optionPosition || '1') - 1
      : -1;

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        var swatchGroup = input.dataset.swatchGroup;
        var isAvailable;

        if (swatchGroup) {
          isAvailable = true;
        } else if (groupOptionIndex >= 0 && (fieldsetIndex === groupOptionIndex || fieldsetIndex === colorFieldsetIndex)) {
          isAvailable = this.variants.some((v) => v.available && v.options[fieldsetIndex] === input.value);
        } else if (groupOptionIndex >= 0) {
          var opts = [...selectedOptions];
          opts[colorFieldsetIndex] = null;
          isAvailable = this.isOptionValueAvailable(input.value, fieldsetIndex, opts);
        } else {
          isAvailable = this.isOptionValueAvailable(input.value, fieldsetIndex, selectedOptions);
        }

        var label = input.closest('label');
        if (label) {
          label.classList.toggle('unavailable', !isAvailable);
          if (isAvailable) label.classList.remove('disabled');
        }
        if (swatchGroup && input.disabled) input.disabled = false;
      });
    });
  }

  selectGroupOption(groupValue) {
    if (!this.variantPicker) return;
    var allInputs = this.variantPicker.querySelectorAll('input[type="radio"]');
    for (var input of allInputs) {
      if (input.dataset.swatchGroup) continue;
      if (input.value !== groupValue || input.checked) continue;
      input.checked = true;
      var label = input.closest('label');
      if (label) {
        label.closest('fieldset')?.querySelectorAll('.option-button-label').forEach(l => l.classList.remove('active'));
        label.classList.add('active');
      }
      this._ignoreChange = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this._ignoreChange = false;
      break;
    }
  }

  updateAddToCartForAvailability() {
    var btn = this.section.querySelector('.product-add-to-cart');
    if (!btn) return;
    var variant = this.findVariantFromInputs();
    if (variant && this.variantInput) this.variantInput.value = variant.id;
    if (variant && variant.available) {
      btn.disabled = false;
      btn.textContent = btn.dataset.addText || 'Add to Cart';
    } else if (variant && !variant.available) {
      btn.disabled = true;
      btn.textContent = btn.dataset.soldOutText || 'Sold Out';
    } else {
      btn.disabled = true;
      btn.textContent = 'Unavailable';
    }
  }

  getSelectedOptions() {
    var opts = [];
    this.variantPicker.querySelectorAll('[data-option-position]').forEach((fs) => {
      var checked = fs.querySelector('input[type="radio"]:checked');
      opts.push(checked ? checked.value : null);
    });
    return opts;
  }

  isOptionValueAvailable(optionValue, optionIndex, selectedOptions) {
    return this.variants.some((variant) => {
      if (variant.options[optionIndex] !== optionValue) return false;
      if (!variant.available) return false;
      for (var i = 0; i < selectedOptions.length; i++) {
        if (i === optionIndex) continue;
        if (selectedOptions[i] && variant.options[i] !== selectedOptions[i]) return false;
      }
      return true;
    });
  }

  findVariantFromInputs() {
    if (!this.variantPicker) return null;
    var selectedOptions = this.getSelectedOptions();
    if (selectedOptions.length === 0 || selectedOptions.every(v => v === null)) return null;
    return this.variants.find((variant) => {
      return variant.options.every((option, index) => {
        return selectedOptions[index] === null || option === selectedOptions[index];
      });
    }) || null;
  }

  onVariantChange(variant) {
    if (this.variantInput) this.variantInput.value = variant.id;
    this.updatePriceDisplay(variant);
    this.updateAddToCartButton(variant);
    this.updateURL(variant);
    this.applyVariantMedia(variant);
    this.updateShipDate(variant);
  }

  updateShipDate(variant) {
    if (!variant) return;
    var shipEl = this.section.querySelector('[data-ship-date]');
    var mapEl = this.section.querySelector('[data-ship-date-map]');
    if (!shipEl || !mapEl) return;
    var map;
    try { map = JSON.parse(mapEl.textContent); } catch (e) { return; }
    var value = map[String(variant.id)];
    if (value && String(value).trim() !== '') {
      shipEl.textContent = value;
      shipEl.style.display = '';
    } else {
      shipEl.textContent = '';
      shipEl.style.display = 'none';
    }
  }

  updatePriceDisplay(variant) {
    this.currentVariant = variant;

    // Update ALL pricing containers (mobile header + main)
    var containers = this.section.querySelectorAll('.product-pricing');
    containers.forEach(function(container) {
      container.dataset.basePrice = variant.price;

      var salePrice = container.querySelector('.price--sale, .price--current');
      var originalPrice = container.querySelector('.price--original:not(.price--with-upgrades)');
      var badge = container.querySelector('.product-discount-badge:not(.product-savings-badge)');

      if (salePrice) {
        salePrice.textContent = this.formatMoney(variant.price);
        salePrice.dataset.price = variant.price;
      }

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (originalPrice) {
          originalPrice.textContent = this.formatMoney(variant.compare_at_price);
          originalPrice.dataset.comparePrice = variant.compare_at_price;
          originalPrice.style.display = '';
        }
        if (badge) {
          badge.textContent = 'Save ' + this.formatMoney(variant.compare_at_price - variant.price);
          badge.style.display = '';
        }
      } else {
        if (originalPrice) originalPrice.style.display = 'none';
        if (badge) badge.style.display = 'none';
      }
    }.bind(this));

    this.updatePriceWithUpgrades();
  }

  updateAddToCartButton(variant) {
    var btn = this.section.querySelector('.product-add-to-cart');
    if (!btn) return;
    if (variant.available) {
      btn.disabled = false;
      btn.textContent = btn.dataset.addText || 'Add to Cart';
    } else {
      btn.disabled = true;
      btn.textContent = btn.dataset.soldOutText || 'Sold Out';
    }
  }

  updateURL(variant) {
    if (!history.replaceState || !variant) return;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({ variant: variant.id }, '', url.toString());
  }

  formatMoney(cents) {
    const n = Number(cents);
    if (!isFinite(n)) return '';
    return '$' + (n / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // ---- GALLERY DOTS ----

  createGalleryDots() {
    if (!this.splide || !this.galleryDotsContainer) return;
    var total = this.splide.length;
    var splide = this.splide;
    var container = this.galleryDotsContainer;
    var self = this;

    // This runs again on every variant switch — drop the previous namespaced
    // 'moved' handler first so listeners don't stack up across rebuilds.
    splide.off('moved.dots');

    // Render dots inside an inner scrollable track so the outer container
    // can act as a fixed-width window showing only N dots at a time.
    container.innerHTML = '';
    var track = document.createElement('div');
    track.className = 'scrambler-gallery-dots-track';
    container.appendChild(track);

    var slideImgs = this.splideElement.querySelectorAll('.splide__slide img');

    for (var i = 0; i < total; i++) {
      var dot = document.createElement('button');
      dot.className = 'scrambler-gallery-dot' + (i === 0 ? ' is-active' : '');
      dot.type = 'button';
      dot.dataset.index = i;

      var slideImg = slideImgs[i];
      if (slideImg) {
        var thumb = document.createElement('img');
        thumb.className = 'scrambler-gallery-dot__img';
        var thumb1x = slideImg.getAttribute('data-thumb-url') || slideImg.src;
        var thumb2x = slideImg.getAttribute('data-thumb-url-2x');
        // Fill the bordered square (photos) vs. default contain (cut-outs),
        // decided by the section mode + per-image Alt-text override.
        if (this.shouldFillThumb(thumb1x, slideImg.getAttribute('alt'))) {
          thumb.className += ' scrambler-gallery-dot__img--jpg';
        }
        thumb.src = thumb1x;
        if (thumb2x) {
          thumb.srcset = thumb1x + ' 1x, ' + thumb2x + ' 2x';
        }
        thumb.alt = '';
        thumb.loading = 'lazy';
        thumb.decoding = 'async';
        dot.appendChild(thumb);
      }

      dot.addEventListener('click', function(e) {
        // Suppress click that follows a drag.
        if (track.dataset.dragMoved === '1') return;
        splide.go(parseInt(this.dataset.index));
      });
      track.appendChild(dot);
    }

    // Drag-to-scroll on the dot strip. Only kicks in after the pointer
    // moves past a 5px threshold; below that, the user is tapping a dot
    // and the inner button's click handler runs normally.
    // We do NOT use setPointerCapture — it would redirect events to the
    // track and prevent clicks on the inner buttons from firing.
    (function attachDragScroll() {
      var isDown = false;
      var startX = 0;
      var startY = 0;
      var startScroll = 0;
      var dragging = false;
      var DRAG_THRESHOLD = 5;

      track.addEventListener('pointerdown', function(e) {
        // Only start tracking on primary button (mouse) or any touch/pen.
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        isDown = true;
        dragging = false;
        startX = e.clientX;
        startY = e.clientY;
        startScroll = track.scrollLeft;
        track.dataset.dragMoved = '0';
      });

      track.addEventListener('pointermove', function(e) {
        if (!isDown) return;
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;
        if (!dragging && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
          // Past the threshold — start dragging. Only treat as a horizontal
          // drag if X movement dominates (so vertical page scrolls aren't hijacked).
          if (Math.abs(dx) > Math.abs(dy)) {
            dragging = true;
            track.dataset.dragMoved = '1';
          } else {
            isDown = false;
            return;
          }
        }
        if (dragging) {
          e.preventDefault();
          track.scrollLeft = startScroll - dx;
        }
      });

      function endDrag() {
        if (!isDown) return;
        isDown = false;
        // Clear the flag on the next tick — after the synthetic click fires.
        var wasDragging = dragging;
        dragging = false;
        if (wasDragging) {
          setTimeout(function() { track.dataset.dragMoved = '0'; }, 0);
        } else {
          track.dataset.dragMoved = '0';
        }
      }
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      window.addEventListener('pointerup', endDrag);
    })();

    function scrollActiveIntoView(idx) {
      var dot = track.children[idx];
      if (!dot) return;
      // Read the visible-count from --dots-visible (set by the section
      // schema). Always use this — never measure track.clientWidth during
      // a variant switch (reflow can give a stale value that snaps the
      // active dot to the right edge and clips the neighbour as a sliver).
      var dotWidth = dot.offsetWidth;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;
      var stride = dotWidth + gap;

      var containerStyles = window.getComputedStyle(container);
      var visibleCount = parseInt(
        containerStyles.getPropertyValue('--dots-visible'),
        10
      );
      if (!visibleCount || isNaN(visibleCount) || visibleCount < 1) {
        visibleCount = 4; // schema default
      }

      var totalDots = track.children.length;
      var maxStart = Math.max(0, totalDots - visibleCount);
      var desiredStart = idx - Math.floor((visibleCount - 1) / 2);
      var start = Math.max(0, Math.min(desiredStart, maxStart));
      track.scrollTo({ left: start * stride, behavior: 'smooth' });
    }

    splide.on('moved.dots', function(newIndex) {
      track.querySelectorAll('.scrambler-gallery-dot').forEach(function(d, idx) {
        d.classList.toggle('is-active', idx === newIndex);
      });
      scrollActiveIntoView(newIndex);
    });

    // Swap the JPG frame state at the START of the move (using the destination
    // index) so the frame is already sized correctly as the new image fades in.
    // Doing this on 'moved' (end) makes the image appear to grow mid-fade.
    splide.off('move.frame');
    splide.on('move.frame', function(newIndex) {
      self.syncGalleryFrameForActiveSlide(newIndex);
    });

    // Set the initial frame state for whatever slide is active now.
    self.syncGalleryFrameForActiveSlide();
  }

  // ---- LIFESTYLE LIGHTBOX ----
  // The bottom grid is now a separate "lifestyle photos" gallery (sourced
  // from product.metafields.custom.lifestyle_images). Clicking a thumbnail
  // opens it in a full-screen lightbox with prev / next / close + keyboard.
  attachImageGridListeners() {
    if (!this.imageGrid) return;
    var self = this;
    var thumbs = Array.from(this.imageGrid.querySelectorAll('.scrambler-grid-thumb'));
    if (!thumbs.length) return;

    var lightbox = this.section.querySelector('[data-lightbox]');
    if (!lightbox) return;

    var imgEl = lightbox.querySelector('[data-lightbox-image]');
    var counter = lightbox.querySelector('[data-lightbox-counter]');
    var closeBtn = lightbox.querySelector('[data-lightbox-close]');
    var prevBtn = lightbox.querySelector('[data-lightbox-prev]');
    var nextBtn = lightbox.querySelector('[data-lightbox-next]');

    var slides = thumbs.map(function(thumb) {
      return {
        src: thumb.getAttribute('data-lightbox-src') || thumb.querySelector('img')?.src,
        srcset: thumb.getAttribute('data-lightbox-srcset') || '',
        alt: thumb.getAttribute('data-lightbox-alt') || ''
      };
    });

    var currentIndex = 0;

    function render(i) {
      currentIndex = (i + slides.length) % slides.length;
      var slide = slides[currentIndex];
      if (imgEl) {
        imgEl.src = slide.src;
        if (slide.srcset) imgEl.setAttribute('srcset', slide.srcset);
        else imgEl.removeAttribute('srcset');
        imgEl.alt = slide.alt;
      }
      if (counter) counter.textContent = (currentIndex + 1) + ' / ' + slides.length;
    }

    function open(i) {
      render(i || 0);
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKey);
    }

    function close() {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    }

    function onKey(e) {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') render(currentIndex - 1);
      else if (e.key === 'ArrowRight') render(currentIndex + 1);
    }

    thumbs.forEach(function(thumb, i) {
      thumb.addEventListener('click', function(e) {
        e.preventDefault();
        open(i);
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (prevBtn) prevBtn.addEventListener('click', function() { render(currentIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function() { render(currentIndex + 1); });

    // Click outside the figure (on the dark backdrop) closes the lightbox
    lightbox.addEventListener('click', function(e) {
      if (e.target === lightbox) close();
    });
  }

  // Go to the carousel slide that matches a variant's featured image
  goToVariantImage(variant) {
    if (!this.splide || !variant || !variant.featured_media) return;
    var mediaId = variant.featured_media.id;
    var slides = this.section.querySelectorAll('.scrambler-main-splide .splide__slide');
    for (var i = 0; i < slides.length; i++) {
      var img = slides[i].querySelector('img');
      if (img && img.dataset.mediaId && parseInt(img.dataset.mediaId) === mediaId) {
        this.splide.go(i);
        return;
      }
    }
  }

  // Escape a string for safe use inside an HTML attribute.
  escapeAttribute(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // Detect a JPG/JPEG from an image URL. Shopify CDN urls put the extension
  // before the query string (…/file.jpg?v=123&width=1200), so we test the
  // path portion only.
  isJpegUrl(url) {
    var path = String(url || '').split('?')[0];
    return /\.jpe?g$/i.test(path);
  }

  // Toggle the JPG frame treatment (padding: 0) based on the currently active
  // main slide. Called on init, on carousel move, and after a rebuild. Each JPG
  // featured image removes the frame padding; PNG cut-outs keep it.
  syncGalleryFrameForActiveSlide(index) {
    if (!this.galleryMain || !this.splide) return;
    var slides = this.splideElement.querySelectorAll('.splide__slide');
    var targetIndex = (typeof index === 'number') ? index : (this.splide.index || 0);
    var targetSlide = slides[targetIndex];
    var img = targetSlide ? targetSlide.querySelector('img') : null;
    var src = img ? (img.getAttribute('src') || '') : '';
    this.galleryMain.classList.toggle('is-jpg-active', this.isJpegUrl(src));
  }

  // Decide whether a thumbnail should "fill" the bordered square.
  // PNG cut-outs are NEVER disturbed except by an explicit #fill Alt tag.
  // Priority:
  //   1. Per-image Alt-text override: #nofill forces off, #fill forces on
  //   2. Section mode 'off' → never fill
  //   3. Per-variant flag (custom.gallery_images_fill) checked → fill this
  //      variant's JPG thumbnails only (PNGs stay as-is)
  //   4. Section mode 'on' → fill all
  //   5. 'auto' → only JPG photos (PNG cut-outs stay as-is)
  shouldFillThumb(url, alt) {
    var altText = String(alt || '').toLowerCase();
    if (altText.indexOf('#nofill') !== -1) return false;
    if (altText.indexOf('#fill') !== -1) return true;
    if (this.thumbnailFillMode === 'off') return false;
    if (this._currentVariantFill === true) return this.isJpegUrl(url);
    if (this.thumbnailFillMode === 'on') return true;
    return this.isJpegUrl(url); // 'auto'
  }

  // Build a main-gallery slide (image) from a variant image object. The
  // data-thumb-url attributes let createGalleryDots() render matching dots.
  buildVariantSlideHtml(image) {
    var src = this.escapeAttribute(image.src);
    var srcset = image.srcset ? ' srcset="' + this.escapeAttribute(image.srcset) + '"' : '';
    var alt = this.escapeAttribute(image.alt || '');
    var thumb = this.escapeAttribute(image.thumb || image.src);
    var thumb2xAttr = image.thumb2x ? ' data-thumb-url-2x="' + this.escapeAttribute(image.thumb2x) + '"' : '';
    return '<li class="splide__slide">' +
      '<img src="' + src + '"' + srcset + ' alt="' + alt + '" class="scrambler-gallery-image" loading="lazy"' +
      ' data-thumb-url="' + thumb + '"' + thumb2xAttr + '></li>';
  }

  // Update the main gallery for a variant.
  //
  // - Variant HAS gallery_images metafield: rebuild the gallery from those
  //   images (native variant image first, then the metafield images).
  // - Variant has NO metafield images: keep the full authored product gallery
  //   and just scroll it to this variant's image (original behaviour, unchanged).
  applyVariantMedia(variant) {
    if (!this.splide) return;
    var variantId = variant && variant.id != null ? variant.id : variant;
    var entry = this.variantMediaMap[String(variantId)];
    var variantImages = entry && entry.images ? entry.images : null;
    var hasMetafieldGallery = !!(variantImages && variantImages.length);

    if (!hasMetafieldGallery) {
      // Fallback: full authored product gallery. Its thumbnails follow the
      // section fill mode (no per-variant flag applies here).
      this._currentVariantFill = null;
      if (this._mediaStateKey && this._mediaStateKey !== '__fallback__') {
        this._mediaStateKey = '__fallback__';
        if (this.authoredSlidesHtml.length) {
          this.splide.remove(function () { return true; });
          this.splide.add(this.authoredSlidesHtml);
          this.createGalleryDots();
        }
      } else {
        this._mediaStateKey = '__fallback__';
      }
      this.goToVariantImage(variant);
      return;
    }

    // Per-variant thumbnail fill flag (custom.gallery_fill_jpg).
    this._currentVariantFill = !!entry.fill;

    // Variant-specific gallery from metafield images.
    var stateKey = variantImages.map(function (image) { return image.src; }).join('|');
    if (stateKey === this._mediaStateKey) return;
    this._mediaStateKey = stateKey;

    var slidesHtml = variantImages.map(this.buildVariantSlideHtml.bind(this));
    if (!slidesHtml.length) return;

    // Rebuild via Splide's remove/add so internal slide state stays correct.
    this.splide.remove(function () { return true; });
    this.splide.add(slidesHtml);

    // Rebuild the thumbnail dots to match the new slide set, reset to first.
    this.createGalleryDots();
    this.splide.go(0);
  }

  // ---- ADD-ONS ----

  attachAddOnListeners() {
    this.section.querySelectorAll('.free-item-checkbox').forEach((cb) => {
      cb.addEventListener('change', (e) => {
        var label = e.target.closest('.pdp-scrambler-addon-item');
        if (label) label.classList.toggle('is-checked', e.target.checked);
      });
    });

    this.section.querySelectorAll('.upgrade-item-checkbox').forEach((cb) => {
      cb.addEventListener('change', (e) => {
        var label = e.target.closest('.pdp-scrambler-addon-item');
        if (label) label.classList.toggle('is-checked', e.target.checked);
        this.updatePriceWithUpgrades();
      });
    });

    this.updatePriceWithUpgrades();
  }

  updatePriceWithUpgrades() {
    var container = this.section.querySelector('.product-information .product-pricing');
    if (!container) return;
    var hasSalePrice = container.querySelector('.price--sale') !== null;
    if (hasSalePrice) return;

    var basePrice = parseInt(container.dataset.basePrice) || (this.currentVariant?.price) || 0;
    var upgradeTotal = 0;
    this.section.querySelectorAll('.upgrade-item-checkbox:checked').forEach((cb) => {
      upgradeTotal += parseInt(cb.dataset.itemPriceValue) || 0;
    });

    var currentPrice = container.querySelector('.price--current');
    var upgradeOriginal = container.querySelector('.price--with-upgrades');
    var savingsBadge = container.querySelector('.product-savings-badge');
    if (!currentPrice) return;

    if (upgradeTotal > 0) {
      currentPrice.textContent = this.formatMoney(basePrice + upgradeTotal);
      if (upgradeOriginal) { upgradeOriginal.textContent = this.formatMoney(basePrice); upgradeOriginal.style.display = ''; }
      if (savingsBadge) { savingsBadge.textContent = '+' + this.formatMoney(upgradeTotal) + ' in upgrades'; savingsBadge.style.display = ''; }
    } else {
      currentPrice.textContent = this.formatMoney(basePrice);
      if (upgradeOriginal) upgradeOriginal.style.display = 'none';
      if (savingsBadge) savingsBadge.style.display = 'none';
    }
  }

  // ---- STANDARD OR PERSONALIZED NAMEPLATE (PRESENTATION ONLY) ----

  attachNameplateChoiceLabels() {
    this.section.querySelectorAll('.product-add-ons-item-wrapper.has-name-input').forEach((wrapper) => {
      var input = wrapper.querySelector('.product-add-ons-name-input');
      var checkbox = wrapper.querySelector('.free-item-checkbox');
      var status = wrapper.querySelector('[data-nameplate-choice-status]');
      var shipping = wrapper.querySelector('[data-nameplate-personalized-note]');
      if (!input) return;

      var render = () => {
        var personalized = Boolean(input.value.trim() && (!checkbox || checkbox.checked));
        if (status) {
          status.textContent = personalized
            ? 'Personalized nameplate selected. This replaces the standard Juiced nameplate at no extra charge.'
            : 'Standard Juiced nameplate included. Enter a name to replace it with a personalized plate for free.';
        }
        if (shipping) {
          shipping.hidden = !personalized;
          shipping.style.display = personalized ? '' : 'none';
        }
      };
      input.addEventListener('input', render);
      input.addEventListener('change', render);
      if (checkbox) checkbox.addEventListener('change', render);
      render();
    });
  }

  // ---- LOCAL ENGRAVING LANGUAGE CHECK (NO NETWORK CALLS) ----

  attachNameplateModeration() {
    this.section.querySelectorAll('.product-add-ons-item-wrapper.has-name-input').forEach((wrapper) => {
      const input = wrapper.querySelector('.product-add-ons-name-input');
      const status = wrapper.querySelector('[data-nameplate-moderation-status]');
      if (!input || !status) return;
      const update = () => this.checkEngravingField(input, status);
      input.addEventListener('input', update);
      input.addEventListener('change', update);
      update();
    });
  }

  checkEngravingField(input, status) {
    // No engraving means the standard Juiced-branded plate, not another SKU.
    const decision = window.JuicedNameplateModeration?.evaluate(input.value);
    const isRejected = Boolean(input.value.trim()) && (decision ? decision.status === 'blocked' : juicedEngravingRejected(input.value));
    const isReview = Boolean(input.value.trim()) && decision?.status === 'review';
    input.setAttribute('aria-invalid', isRejected ? 'true' : 'false');
    status.style.color = isReview ? '#855600' : '#b42318';
    status.textContent = isRejected
      ? 'This engraving contains language we cannot accept. Please choose another name or phrase.'
      : isReview ? 'This engraving may require review by Juiced before production. We may contact you.' : '';
    status.hidden = !isRejected && !isReview;
    return !isRejected;
  }

  validateEngravingBeforeAddToCart() {
    let isValid = true;
    for (const wrapper of this.section.querySelectorAll('.product-add-ons-item-wrapper.has-name-input')) {
      const input = wrapper.querySelector('.product-add-ons-name-input');
      const status = wrapper.querySelector('[data-nameplate-moderation-status]');
      const checkbox = wrapper.querySelector('.free-item-checkbox');
      if (!input || !status || (checkbox && !checkbox.checked)) continue;
      if (!this.checkEngravingField(input, status)) {
        if (isValid) input.focus();
        isValid = false;
      }
    }
    // Legacy personalization form, when present, gets the same guard.
    const legacy = this.section.querySelector('.pdp-personalization-radio:checked');
    const legacyInput = legacy && this.section.querySelector('.pdp-personalization-input');
    if (legacyInput && juicedEngravingRejected(legacyInput.value)) {
      const legacyError = this.section.querySelector('.pdp-personalization-error');
      if (legacyError) {
        legacyError.textContent = 'This engraving contains language we cannot accept. Please choose another name or phrase.';
        legacyError.classList.add('is-visible');
      }
      legacyInput.focus();
      isValid = false;
    }
    return isValid;
  }

  // ---- NAMEPLATE PREVIEW (PRESENTATION ONLY) ----

  attachNameplatePreview() {
    this.section.querySelectorAll('[data-nameplate-preview]').forEach((preview) => {
      var wrapper = preview.closest('.has-name-input');
      var input = wrapper ? wrapper.querySelector('.product-add-ons-name-input') : null;
      var svgText = preview.querySelector('[data-nameplate-preview-text]');
      var censor = preview.querySelector('[data-nameplate-preview-censor]');
      var hint = preview.querySelector('[data-nameplate-preview-hint]');
      var visual = preview.querySelector('.nameplate-preview__visual');
      if (!input || !svgText) return;

      var render = () => {
        // Do not alter input.value: Shopify must receive the customer's
        // original engraving instruction rather than the visual approximation.
        var originalName = input.value.trim();
        var rejected = Boolean(originalName) && juicedEngravingRejected(originalName);
        var review = Boolean(originalName) && window.JuicedNameplateModeration?.evaluate(originalName)?.status === 'review';
        // Never render disallowed engraving text, even underneath the censor bar.
        var displayName = rejected ? '' : (originalName ? originalName.toUpperCase() : 'JUICED');
        svgText.textContent = displayName; // safe: text only, never SVG markup
        preview.classList.toggle('nameplate-preview--censored', rejected);
        if (censor) censor.hidden = !rejected;
        svgText.setAttribute('font-size', '94');
        // Fit longer names between the two mounting bolts.
        var svgWidth = typeof svgText.getComputedTextLength === 'function'
          ? svgText.getComputedTextLength()
          : 0;
        if (svgWidth > 760) {
          svgText.setAttribute('font-size', String(Math.max(34, Math.floor(94 * 760 / svgWidth))));
        }
        preview.dataset.hasName = originalName ? 'true' : 'false';
        if (hint) {
          hint.textContent = rejected
            ? 'This engraving cannot be personalized. Choose another name to continue.'
            : originalName
              ? (review ? 'Review required before production. Juiced may contact you. Illustrative preview only.' : 'Illustrative preview only. Final engraving font and finish may vary.')
              : 'Standard Juiced nameplate included. Enter a name to preview your personalized replacement.';
        }
        if (visual) {
          visual.setAttribute('aria-label', rejected
            ? 'Nameplate preview censored because the engraving contains disallowed language'
            : originalName
              ? 'Approximate engraving preview: ' + displayName
              : 'Approximate standard Juiced nameplate; enter a name to preview personalization');
        }
      };
      input.addEventListener('input', render);
      input.addEventListener('change', render);
      render();
      // Refit after asynchronous Google Font loading.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(render).catch(function () {});
      }
    });
  }

  // ---- PERSONALIZATION ----

  attachPersonalizationListeners() {
    var section = this.section;
    var radio = section.querySelector('.pdp-personalization-radio');
    var fields = section.querySelector('.pdp-personalization-fields');
    var textInput = section.querySelector('.pdp-personalization-input');
    var charCount = section.querySelector('.pdp-personalization-char-count');
    var termsCheckbox = section.querySelector('.pdp-personalization-terms-checkbox');
    var termsLink = section.querySelector('.pdp-personalization-terms-link');
    var modalOverlay = section.querySelector('.pdp-terms-modal-overlay');
    var modalAcceptBtn = section.querySelector('.pdp-terms-modal-accept');
    var modalCancelBtn = section.querySelector('.pdp-terms-modal-cancel');
    var modalCloseBtn = section.querySelector('.pdp-terms-modal-close');
    var modalAcceptCheckbox = section.querySelector('.pdp-terms-modal-accept-checkbox');
    var self = this;

    if (!radio || !fields) return;

    // Fields are always visible (per Figma)
    fields.classList.add('is-visible');

    // Radio can only be checked when terms + letter fill are both set
    var styleSelect = section.querySelector('.pdp-personalization-select[data-field="letter-style"]');

    // ---- Custom dropdown wiring (replaces the native <select> with a styled
    // listbox so we can paint the yellow-green hover highlight) ----
    var customSelect = section.querySelector('.pdp-custom-select');
    if (customSelect && styleSelect) {
      var toggle = customSelect.querySelector('.pdp-custom-select__toggle');
      var label = customSelect.querySelector('.pdp-custom-select__label');
      var list = customSelect.querySelector('.pdp-custom-select__list');
      var options = list ? Array.from(list.querySelectorAll('.pdp-custom-select__option')) : [];
      var placeholderText = label ? (label.getAttribute('data-placeholder') || label.textContent) : '';

      function closeCustom() {
        customSelect.classList.remove('is-open');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
      function openCustom() {
        customSelect.classList.add('is-open');
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
      }
      function pickOption(opt) {
        if (!opt || opt.classList.contains('is-unavailable')) return;
        options.forEach(function(o) { o.classList.remove('is-selected'); });
        opt.classList.add('is-selected');
        var val = opt.getAttribute('data-value') || '';
        styleSelect.value = val;
        if (label) label.textContent = opt.textContent;
        customSelect.classList.toggle('is-selected', !!val);
        // Trigger native change so existing listeners (radio state, pulse clear, etc.) fire
        styleSelect.dispatchEvent(new Event('change', { bubbles: true }));
        closeCustom();
      }

      if (toggle) {
        toggle.addEventListener('click', function(e) {
          e.preventDefault();
          if (customSelect.classList.contains('is-open')) closeCustom();
          else openCustom();
        });
      }

      options.forEach(function(opt) {
        opt.addEventListener('click', function(e) {
          e.stopPropagation();
          pickOption(opt);
        });
      });

      document.addEventListener('click', function(e) {
        if (!customSelect.contains(e.target)) closeCustom();
      });

      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') closeCustom();
      });

      // Reflect initial state (if the native select has a value preset)
      if (styleSelect.value) {
        var initial = options.find(function(o) { return o.getAttribute('data-value') === styleSelect.value; });
        if (initial) pickOption(initial);
      }
    }

    function canCheckRadio() {
      var hasName = textInput && textInput.value.trim().length > 0;
      var hasStyle = styleSelect && styleSelect.value;
      var hasTerms = termsCheckbox && termsCheckbox.checked;
      return hasName && hasStyle && hasTerms;
    }

    function markEmptyFields() {
      // Flag empty required fields with a class so CSS can paint them grey
      if (textInput) {
        textInput.classList.toggle('needs-value', !textInput.value.trim());
      }
      if (styleSelect) {
        styleSelect.classList.toggle('needs-value', !styleSelect.value);
      }
      if (termsCheckbox) {
        var termsWrap = termsCheckbox.closest('.pdp-personalization-terms-wrap');
        if (termsWrap) termsWrap.classList.toggle('needs-value', !termsCheckbox.checked);
      }
    }

    function updateRadioState() {
      markEmptyFields();
      if (canCheckRadio()) {
        radio.disabled = false;
        radio.classList.remove('is-disabled');
      } else {
        radio.disabled = true;
        radio.checked = false;
        radio.classList.add('is-disabled');
      }
    }

    // Listen for changes on all required fields
    [textInput, styleSelect, termsCheckbox].forEach(function(el) {
      if (el) {
        el.addEventListener('input', updateRadioState);
        el.addEventListener('change', updateRadioState);
      }
    });

    // Initial state - radio disabled
    updateRadioState();

    var errorEl = section.querySelector('.pdp-personalization-error');

    function showError(msg) {
      if (errorEl) {
        errorEl.textContent = msg;
        errorEl.classList.add('is-visible');
      }
    }

    function hideError() {
      if (errorEl) {
        errorEl.classList.remove('is-visible');
      }
    }

    function pulseEmptyFields() {
      var targets = [];
      if (textInput && !textInput.value.trim()) targets.push(textInput);
      if (styleSelect && !styleSelect.value) targets.push(styleSelect);
      if (termsCheckbox && !termsCheckbox.checked) {
        var termsWrap = termsCheckbox.closest('.pdp-personalization-terms-wrap');
        if (termsWrap) targets.push(termsWrap);
      }
      targets.forEach(function(el) {
        el.classList.remove('is-pulsing');
        // Force reflow so the animation restarts on repeat clicks
        // eslint-disable-next-line no-unused-expressions
        el.offsetWidth;
        el.classList.add('is-pulsing');
      });
      setTimeout(function() {
        targets.forEach(function(el) { el.classList.remove('is-pulsing'); });
      }, 1200);
    }

    // When the radio is `disabled` the input swallows clicks silently.
    // Listen on the parent <label> so the pulse + error still trigger.
    var radioRow = radio.closest('.pdp-personalization-radio-row') || radio;
    radioRow.addEventListener('click', function(e) {
      if (!canCheckRadio()) {
        e.preventDefault();
        pulseEmptyFields();
        var errors = [];
        if (!textInput || !textInput.value.trim()) errors.push('Please enter a name');
        if (!styleSelect || !styleSelect.value) errors.push('please select a letter fill option');
        if (!termsCheckbox || !termsCheckbox.checked) errors.push('and accept the terms');
        showError(errors.join(', ') + '.');
      } else {
        hideError();
      }
    });

    // Hide error when all requirements are met
    [textInput, styleSelect, termsCheckbox].forEach(function(el) {
      if (el) {
        el.addEventListener('input', function() {
          if (canCheckRadio()) hideError();
        });
        el.addEventListener('change', function() {
          if (canCheckRadio()) hideError();
        });
      }
    });

    // Letter mask: allow only letters, numbers, and spaces in the name input
    if (textInput) {
      textInput.addEventListener('input', function(e) {
        var cleaned = textInput.value.replace(/[^A-Za-z0-9 ]/g, '');
        if (textInput.value !== cleaned) {
          textInput.value = cleaned;
        }
      });
    }

    // Description Read More / Show Less toggle
    var descWrap = section.querySelector('.pdp-personalization-description');
    var descToggle = section.querySelector('.pdp-personalization-description-toggle');
    if (descWrap && descToggle) {
      descToggle.addEventListener('click', function() {
        var expanded = descToggle.dataset.expanded === 'true';
        if (expanded) {
          descWrap.classList.add('is-collapsed');
          descToggle.textContent = 'Read More';
          descToggle.dataset.expanded = 'false';
        } else {
          descWrap.classList.remove('is-collapsed');
          descToggle.textContent = 'Show Less';
          descToggle.dataset.expanded = 'true';
        }
      });
    }

    // Character count
    if (textInput && charCount) {
      var maxLen = parseInt(textInput.getAttribute('maxlength')) || 12;
      textInput.addEventListener('input', function() {
        charCount.textContent = textInput.value.length + '/' + maxLen;
      });
    }

    // Terms link opens modal
    if (termsLink && modalOverlay) {
      termsLink.addEventListener('click', function(e) {
        e.preventDefault();
        modalOverlay.classList.add('is-open');
        if (modalAcceptCheckbox) modalAcceptCheckbox.checked = false;
      });
    }

    // Terms checkbox opens modal
    if (termsCheckbox && modalOverlay) {
      termsCheckbox.addEventListener('change', function() {
        if (termsCheckbox.checked && !self.personalizationConfirmed) {
          termsCheckbox.checked = false;
          modalOverlay.classList.add('is-open');
          if (modalAcceptCheckbox) modalAcceptCheckbox.checked = false;
        }
      });
    }

    // Modal accept
    if (modalAcceptBtn && modalOverlay) {
      modalAcceptBtn.addEventListener('click', function() {
        if (modalAcceptCheckbox && !modalAcceptCheckbox.checked) return;
        self.personalizationConfirmed = true;
        if (termsCheckbox) termsCheckbox.checked = true;
        modalOverlay.classList.remove('is-open');
      });
    }

    // Modal cancel
    if (modalCancelBtn && modalOverlay) {
      modalCancelBtn.addEventListener('click', function() {
        self.personalizationConfirmed = false;
        if (termsCheckbox) termsCheckbox.checked = false;
        modalOverlay.classList.remove('is-open');
      });
    }

    // Modal close X
    if (modalCloseBtn && modalOverlay) {
      modalCloseBtn.addEventListener('click', function() {
        modalOverlay.classList.remove('is-open');
      });
    }

    // Close modal on overlay click
    if (modalOverlay) {
      modalOverlay.addEventListener('click', function(e) {
        if (e.target === modalOverlay) {
          modalOverlay.classList.remove('is-open');
        }
      });
    }
  }

  getPersonalizationItem() {
    var radio = this.section.querySelector('.pdp-personalization-radio');
    if (!radio || !radio.checked) return null;

    var textInput = this.section.querySelector('.pdp-personalization-input');
    var styleSelect = this.section.querySelector('.pdp-personalization-select[data-field="letter-style"]');
    var nameplateVariantId = this.section.querySelector('[data-nameplate-variant-id]');

    if (!nameplateVariantId) return null;

    var name = textInput ? textInput.value.trim() : '';
    // The optional legacy personalization block must not create a blank plate.
    if (!name) return null;
    var letterStyle = styleSelect ? styleSelect.value : '';

    var properties = {
      'Name': name,
      'Letter Fill': letterStyle,
      '_note': nameplateVariantId.dataset.nameplateNote || 'Ships in individual box, within 3 weeks'
    };

    // If the style select has a data-variant-id on its selected option, use that instead
    var variantIdToUse = parseInt(nameplateVariantId.dataset.nameplateVariantId);
    if (styleSelect && styleSelect.selectedIndex >= 0) {
      var selectedOption = styleSelect.options[styleSelect.selectedIndex];
      if (selectedOption && selectedOption.dataset.variantId) {
        variantIdToUse = parseInt(selectedOption.dataset.variantId);
      }
    }

    return {
      id: variantIdToUse,
      quantity: 1,
      properties: properties
    };
  }

  validatePersonalization() {
    var radio = this.section.querySelector('.pdp-personalization-radio');
    if (!radio || !radio.checked) return true; // Not selected, no validation needed

    var errorEl = this.section.querySelector('.pdp-personalization-error');
    var textInput = this.section.querySelector('.pdp-personalization-input');
    var styleSelect = this.section.querySelector('.pdp-personalization-select[data-field="letter-style"]');
    var termsCheckbox = this.section.querySelector('.pdp-personalization-terms-checkbox');

    var errors = [];
    if (!textInput || !textInput.value.trim()) errors.push('Please enter a name.');
    if (styleSelect && !styleSelect.value) errors.push('Please select a letter fill option.');
    if (termsCheckbox && !termsCheckbox.checked) errors.push('Please accept the personalization terms.');

    if (errors.length > 0 && errorEl) {
      errorEl.textContent = errors[0];
      errorEl.classList.add('is-visible');
      return false;
    }

    if (errorEl) errorEl.classList.remove('is-visible');
    return true;
  }

  // ---- FORM SUBMIT ----

  attachFormSubmitHandler() {
    if (!this.productForm) return;

    this.productForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      var cartError = this.section.querySelector('[data-product-cart-error]');
      if (cartError) cartError.hidden = true;
      var cartDiagnostics = this.section.querySelector('[data-product-cart-diagnostics]');
      if (cartDiagnostics) cartDiagnostics.hidden = true;
      var cartDiagnosticText = this.section.querySelector('[data-product-cart-diagnostic-text]');
      if (cartDiagnosticText) cartDiagnosticText.textContent = '';

      var addToCartBtn = this.section.querySelector('.product-add-to-cart');
      var currentVariant = this.findVariantFromInputs();

      if (!currentVariant || !currentVariant.available) {
        if (addToCartBtn) addToCartBtn.textContent = currentVariant ? 'Sold Out' : 'Unavailable';
        return;
      }

      // Validate personalization and private language policy before any cart
      // mutation. Without the private app proxy switch, only the current
      // informational content-policy notice is shown.
      if (!this.validatePersonalization()) return;
      if (!(await this.validateEngravingBeforeAddToCart())) return;

      if (addToCartBtn) {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Adding...';
      }

      var failedCartDebug = null;
      try {
        // Build items with main product first so it appears on top in the
        // cart drawer list ahead of upgrades / personalization.
        var itemsToAdd = [];
        // Parallel array of human-readable info for each itemsToAdd entry
        var itemsMeta = [];

        // A unique grouping property stops identical bike variants from merging and
        // losing the association with a particular engraving.
        var bundleId = (window.crypto && typeof window.crypto.randomUUID === 'function')
          ? window.crypto.randomUUID()
          : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
        var mainProperties = { _is_main_product: 'true', _juiced_bundle_id: bundleId };
        var mainVariantId = parseInt(currentVariant.id, 10);
        var selectedFreeItems = [];

        // No engraving means the stock Juiced-branded plate remains included
        // with the bike. Only a personalized replacement is an extra SKU.
        this.section.querySelectorAll('.free-item-checkbox:checked').forEach((cb) => {
          var wrap = cb.closest('.product-add-ons-item-wrapper');
          var nameInput = wrap ? wrap.querySelector('.product-add-ons-name-input') : null;
          var name = nameInput ? nameInput.value.trim() : '';
          var requiresName = cb.dataset.requiresName === 'true';
          if (requiresName && !name) return;
          // Pilot scope: only personalized gifts become real order lines.
          // Other included accessories remain presentational, even if an
          // old free-item metaobject links to a legacy $0 Shopify product.
          var variantId = requiresName ? parseInt(cb.dataset.variantId, 10) : 0;
          // Never silently accept a personalized gift that cannot be fulfilled.
          if (requiresName && !variantId) {
            throw new Error('The free nameplate is not available for online checkout yet. Please try again later.');
          }
          selectedFreeItems.push({
            checkbox: cb,
            name: name,
            variantId: variantId,
            requiresName: requiresName
          });
        });

        // Included-in-the-box accessories without a linked product remain visual
        // properties on the bike. Real linked variants are NOT duplicated here.
        var displayIndex = 0;
        selectedFreeItems.forEach((freeItem) => {
          if (freeItem.variantId) return;
          var cb = freeItem.checkbox;
          var name = cb.dataset.itemName || '';
          if (!name) return;
          displayIndex++;
          mainProperties['_free_' + displayIndex + '_name'] = name;
          mainProperties['_free_' + displayIndex + '_image'] = cb.dataset.itemImage || '';
          mainProperties['_free_' + displayIndex + '_price'] = cb.dataset.itemPrice || '';
        });

        // A standard Juiced-branded plate ships ON the bike when no
        // personalized engraving was selected. This is display-only and
        // intentionally creates no separate order/fulfillment line.
        var nameplateCheckbox = this.section.querySelector('.free-item-checkbox[data-requires-name="true"]');
        var nameplateWrap = nameplateCheckbox ? nameplateCheckbox.closest('.has-name-input') : null;
        var nameplateInput = nameplateWrap ? nameplateWrap.querySelector('.product-add-ons-name-input') : null;
        var isPersonalized = Boolean(
          nameplateCheckbox && nameplateCheckbox.checked &&
          nameplateInput && nameplateInput.value.trim()
        );
        if (nameplateCheckbox && !isPersonalized) {
          displayIndex++;
          mainProperties['_free_' + displayIndex + '_name'] = 'Standard Juiced Nameplate';
          mainProperties['_free_' + displayIndex + '_image'] = nameplateCheckbox.dataset.itemImage || '';
          mainProperties['_free_' + displayIndex + '_price'] = '';
        }

        itemsToAdd.push({
          id: mainVariantId,
          quantity: 1,
          properties: mainProperties
        });
        itemsMeta.push({
          source: 'main',
          name: (window.__pdpProductTitle || document.title || 'Main product'),
          variantId: mainVariantId
        });

        // Shopify nested cart lines: the parent is in this SAME add request.
        // Shopify automatically removes each child when its parent is removed.
        selectedFreeItems.forEach((freeItem) => {
          if (!freeItem.variantId) return;
          var cb = freeItem.checkbox;
          var freeProps = {
            _free_with: String(mainVariantId),
            _juiced_bundle_id: bundleId
          };
          if (freeItem.name) freeProps.Name = freeItem.name;
          if ([42894604927053, 67624804974669].includes(Number(freeItem.variantId)) && window.JuicedNameplateModeration?.evaluate(freeItem.name)?.status === 'review') {
            freeProps._juiced_nameplate_review = 'Pending manual review';
          }
          if (cb.dataset.itemNote) freeProps._note = cb.dataset.itemNote;
          // Keep the original free-item thumbnail and merchandising label in
          // hidden line properties for the custom cart drawer's nested display.
          // The separate Shopify SKU and parent_id remain authoritative.
          if (cb.dataset.itemName) freeProps._juiced_free_item_title = cb.dataset.itemName;
          if (cb.dataset.itemImage) freeProps._juiced_free_item_image = cb.dataset.itemImage;
          itemsToAdd.push({
            id: freeItem.variantId,
            quantity: 1,
            parent_id: mainVariantId,
            properties: freeProps
          });
          itemsMeta.push({
            source: 'free',
            name: cb.dataset.itemName || ('Free item #' + freeItem.variantId),
            variantId: freeItem.variantId
          });
        });

        var checkedUpgrades = this.section.querySelectorAll('.upgrade-item-checkbox:checked');
        checkedUpgrades.forEach((cb) => {
          var variantId = parseInt(cb.dataset.variantId, 10);
          if (!variantId) return;
          itemsToAdd.push({ id: variantId, quantity: 1 });
          itemsMeta.push({
            source: 'upgrade',
            name: cb.dataset.itemName || cb.dataset.upgradeName || ('Upgrade #' + variantId),
            variantId: variantId
          });
        });

        // Avoid a second nameplate if both the free-item and legacy
        // personalization blocks are ever enabled by merchandising.
        var personalizationItem = selectedFreeItems.some((item) => item.requiresName && item.variantId)
          ? null
          : this.getPersonalizationItem();
        if (personalizationItem) {
          itemsToAdd.push(personalizationItem);
          itemsMeta.push({
            source: 'personalization',
            name: 'Personalization',
            variantId: personalizationItem.id
          });
        }

        // Add everything in one request so Shopify preserves the array order
        var addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: itemsToAdd })
        });
        if (!addResponse.ok) {
          var addError = await addResponse.json().catch(function() { return {}; });
          console.error('Cart add failed:', addResponse.status, addError);
          // Read-only debugging info; no follow-up add, probe, or cart mutation.
          failedCartDebug = {
            httpStatus: addResponse.status,
            shopifyMessage: addError.description || addError.message || '',
            requestedItems: itemsToAdd.map(function (item) {
              return {
                variantId: item.id,
                quantity: item.quantity,
                parentId: item.parent_id || null,
                hasEngravingName: Boolean(item.properties && item.properties.Name)
              };
            })
          };

          // Never probe with extra cart additions or clear the shopper's cart:
          // failed bundle submissions must leave existing cart contents alone.
          if (addResponse.status === 429) {
            throw new Error('Too many cart requests. Please wait and try again.');
          }
          throw new Error(addError.description || addError.message || 'Failed to add to cart');
        }

        // Note: the cart drawer template renders main-product line items
        // first (see snippets/cart-drawer-juiced.liquid), so no client-side
        // reordering is required here.

        this.onAddToCartSuccess();
      } catch (error) {
        console.error('Add to cart error:', error);
        var cartError = this.section.querySelector('[data-product-cart-error]');
        if (cartError) {
          cartError.textContent = error.message || 'Unable to add to cart. Please try again.';
          cartError.hidden = false;
        }
        var diagnosticsPanel = this.section.querySelector('[data-product-cart-diagnostics]');
        var diagnosticText = this.section.querySelector('[data-product-cart-diagnostic-text]');
        if (diagnosticsPanel && diagnosticText) {
          diagnosticText.textContent = JSON.stringify(
            failedCartDebug || {message: error.message || String(error), noNetworkResponse: true},
            null,
            2
          );
          diagnosticsPanel.hidden = false;
        }
        if (addToCartBtn) {
          addToCartBtn.textContent = 'Error - Try Again';
          setTimeout(() => {
            addToCartBtn.disabled = false;
            addToCartBtn.textContent = addToCartBtn.dataset.addText || 'Add to Cart';
          }, 2000);
        }
      }
    });
  }

  async onAddToCartSuccess() {
    var btn = this.section.querySelector('.product-add-to-cart');
    if (btn) {
      btn.textContent = 'Added!';
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = btn.dataset.addText || 'Add to Cart';
      }, 1500);
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:open'));
    await this.refreshCartDrawer();

    var cartDrawer = document.getElementById('CartDrawer');
    var cartOverlay = document.getElementById('CartDrawerOverlay');
    if (cartDrawer) { cartDrawer.classList.add('active'); cartDrawer.setAttribute('aria-hidden', 'false'); }
    if (cartOverlay) cartOverlay.classList.add('active');

    this.updateCartCount();
  }

  async refreshCartDrawer() {
    try {
      var response = await fetch(window.location.pathname + '?sections=cart-drawer-juiced');
      if (!response.ok) throw new Error('Failed to fetch cart drawer');
      var data = await response.json();
      var html = data['cart-drawer-juiced'];
      if (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newItems = doc.getElementById('CartItems');
        var currentItems = document.getElementById('CartItems');
        if (newItems && currentItems) currentItems.innerHTML = newItems.innerHTML;

        var newTotal = doc.getElementById('CartTotal');
        var currentTotal = document.getElementById('CartTotal');
        if (newTotal && currentTotal) currentTotal.textContent = newTotal.textContent;

        var newFooter = doc.querySelector('.cart-drawer-footer');
        var currentFooter = document.querySelector('.cart-drawer-footer');
        if (newFooter && currentFooter) currentFooter.className = newFooter.className;

        this.attachCartDrawerListeners();
      }
    } catch (error) {
      console.error('Failed to refresh cart drawer:', error);
    }
  }

  attachCartDrawerListeners() {
    var cartDrawer = document.getElementById('CartDrawer');
    if (!cartDrawer) return;
    var self = this;
    cartDrawer.querySelectorAll('[data-action="decrease-quantity"]').forEach(btn => {
      btn.addEventListener('click', (e) => self.handleQuantityChange(e, -1));
    });
    cartDrawer.querySelectorAll('[data-action="increase-quantity"]').forEach(btn => {
      btn.addEventListener('click', (e) => self.handleQuantityChange(e, 1));
    });
    cartDrawer.querySelectorAll('[data-action="remove-item"]').forEach(btn => {
      btn.addEventListener('click', (e) => self.handleRemoveItem(e));
    });
  }

  async handleQuantityChange(e, delta) {
    var line = parseInt(e.currentTarget.dataset.line);
    var input = document.querySelector('[data-quantity-input][data-line="' + line + '"]');
    if (!input) return;
    await this.updateCartLine(line, Math.max(0, (parseInt(input.value) || 1) + delta));
  }

  async handleRemoveItem(e) {
    await this.updateCartLine(parseInt(e.currentTarget.dataset.line), 0);
  }

  async updateCartLine(line, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      await this.refreshCartDrawer();
      await this.updateCartCount();
    } catch (error) {
      console.error('Failed to update cart line:', error);
    }
  }

  async updateCartCount() {
    try {
      var response = await fetch('/cart.js');
      var cart = await response.json();
      // Keep storefront badge consistent with the header's top-level-only
      // count; the cart and checkout still contain the real nested nameplate.
      var cartDisplayCount = Array.isArray(cart.items)
        ? cart.items.reduce(function (count, item) {
            return count + (item.parent_relationship && item.parent_relationship.parent_key
              ? 0
              : (Number(item.quantity) || 0));
          }, 0)
        : (Number(cart.item_count) || 0);
      document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble').forEach(el => {
        el.textContent = cartDisplayCount;
        if (el.hasAttribute('data-cart-count')) el.dataset.cartCount = cartDisplayCount;
        el.classList.toggle('hidden', cartDisplayCount === 0);
      });
      document.querySelectorAll('.header-actions__cart-icon').forEach(function (icon) {
        icon.classList.toggle('header-actions__cart-icon--has-cart', cartDisplayCount > 0);
      });
    } catch (error) {}
  }

  destroy() {
    if (this.splide) this.splide.destroy();
  }
}

// Init
function initScramblerPDPSections() {
  var sections = document.querySelectorAll('[data-testid="pdp-scrambler-detail"]');
  sections.forEach(function(section) {
    new PDPScramblerDetail(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initScramblerPDPSections);
} else {
  initScramblerPDPSections();
}

document.addEventListener('shopify:section:load', function(event) {
  var section = event.target;
  if (section && section.querySelector('[data-testid="pdp-scrambler-detail"]')) {
    new PDPScramblerDetail(section);
  }
});

